import type { App, ComponentPublicInstance } from 'vue';

const LOG_PREFIX = '[account]';

/**
 * Best-effort human-readable component name for production diagnostics.
 * Works for both Options API and `<script setup>` components.
 */
function describeComponent(instance: ComponentPublicInstance | null): string {
  if (instance === null) {
    return 'unknown';
  }

  const type = instance.$.type as
    | { name?: string, __name?: string, __file?: string }
    | undefined;

  return type?.name ?? type?.__name ?? type?.__file ?? 'anonymous';
}

/**
 * Installs global error reporting for the account SPA.
 *
 * A single component/effect error must never wedge the whole SPA. Vue routes
 * render, watcher, lifecycle and scheduler errors through `errorHandler`; by
 * handling them here (and not rethrowing) the scheduler keeps flushing and the
 * UI stays interactive instead of freezing on the next navigation.
 *
 * The handler also logs enough context (the failing component and the Vue hook
 * reported in `info`) to pinpoint the source directly from a production console.
 */
export function installErrorReporting(app: App): void {
  app.config.errorHandler = (err, instance, info): void => {
    console.error(
      `${LOG_PREFIX} Vue error [${info}] in <${describeComponent(instance)}>`,
      err,
    );
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('unhandledrejection', (event) => {
      console.error(`${LOG_PREFIX} Unhandled promise rejection`, event.reason);
    });
  }
}
